/**
 * Multi-Product Tabs Controller
 *
 * Handles tab switching between products on the PDP using
 * Shopify's Section Rendering API for AJAX content updates.
 */

(function() {
  'use strict';

  const SELECTORS = {
    tabsContainer: '[data-multi-product-tabs]',
    tab: '[data-product-handle]',
    // Elements to update when switching products
    gallery: '.PBFCM-PDP-product-gallery, .product__media-wrapper',
    title: '.product__title',
    productInfo: '.product__info-wrapper',
    priceWrapper: '[id^="price-"]',
    skuWrapper: '[id^="sku-"]',
    productForm: '[id^="product-form-"]',
    variantPicker: 'variant-selects, variant-radios',
    buyButtons: '.product-form__buttons, .buy-buttons',
    description: '.product__description, .accordion',
    inventoryStatus: '.inventory-status, [data-inventory-status]',
    // PBFCM-specific selectors (purchase content, upsells, config)
    purchaseContent: '.product-purchase-content',
    frequencySelector: '.PBFCM-PDP-frequency-selector',
    upsellContainer: '.PBFCM-PDP-upsell-container',
    cartSummary: '.PBFCM-PDP-cart-summary'
  };

  class MultiProductTabs {
    constructor(container) {
      this.container = container;
      this.sectionId = container.dataset.sectionId;
      this.tabs = container.querySelectorAll(SELECTORS.tab);
      this.isLoading = false;

      this.bindEvents();
    }

    bindEvents() {
      this.tabs.forEach(tab => {
        tab.addEventListener('click', (e) => this.handleTabClick(e));
      });
    }

    handleTabClick(event) {
      const tab = event.currentTarget;
      const productHandle = tab.dataset.productHandle;
      const productUrl = tab.dataset.productUrl;

      // Don't reload if clicking the active tab
      if (tab.classList.contains('active') || this.isLoading) {
        return;
      }

      this.switchProduct(productUrl, productHandle, tab);
    }

    async switchProduct(productUrl, productHandle, clickedTab) {
      this.setLoading(true);

      // Preserve straight-to-checkout setting (page-level, not product-level)
      // This is a metafield on the page, so it gets lost when fetching via product URL
      const stcEl = document.getElementById('PBFCM-straight-to-checkout');
      const originalStraightToCheckout = stcEl ? stcEl.value : null;

      try {
        const sectionHtml = await this.fetchProductSection(productUrl);
        this.updatePageContent(sectionHtml);
        this.updateActiveTab(clickedTab);
        this.reinitializeComponents(originalStraightToCheckout);
      } catch (error) {
        console.error('Multi-Product Tabs: Error switching product', error);
      } finally {
        this.setLoading(false);
      }
    }

    async fetchProductSection(productUrl) {
      const url = `${productUrl}?section_id=${this.sectionId}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch product section: ${response.status}`);
      }

      return await response.text();
    }

    updatePageContent(html) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newSection = doc.querySelector(`#shopify-section-${this.sectionId}`);

      if (!newSection) {
        console.error('Multi-Product Tabs: Could not find section in response');
        return;
      }

      // Get the current section
      const currentSection = document.querySelector(`#shopify-section-${this.sectionId}`);

      if (!currentSection) {
        console.error('Multi-Product Tabs: Could not find current section');
        return;
      }

      // Update specific elements instead of replacing the entire section
      // This preserves the tabs state and provides smoother transitions

      // Update product gallery
      this.replaceElement(currentSection, newSection, SELECTORS.gallery);

      // Update product title
      this.replaceElement(currentSection, newSection, SELECTORS.title);

      // Update price
      this.replaceElement(currentSection, newSection, SELECTORS.priceWrapper);

      // Update SKU
      this.replaceElement(currentSection, newSection, SELECTORS.skuWrapper);

      // Update product form
      this.replaceElement(currentSection, newSection, SELECTORS.productForm);

      // Update variant picker
      this.replaceElement(currentSection, newSection, SELECTORS.variantPicker);

      // Update buy buttons
      this.replaceElement(currentSection, newSection, SELECTORS.buyButtons);

      // Update description
      this.replaceElement(currentSection, newSection, SELECTORS.description);

      // Update inventory status
      this.replaceElement(currentSection, newSection, SELECTORS.inventoryStatus);

      // Update PBFCM-specific elements (purchase content, upsells, cart summary)
      // These contain the hidden fields for variant/product IDs and upsell data
      this.replaceElement(currentSection, newSection, SELECTORS.purchaseContent);
      this.replaceElement(currentSection, newSection, SELECTORS.frequencySelector);
      this.replaceElement(currentSection, newSection, SELECTORS.upsellContainer);
      this.replaceElement(currentSection, newSection, SELECTORS.cartSummary);

      // Update product info wrapper (fallback for broader updates)
      // Only if specific updates didn't work
      const galleryUpdated = currentSection.querySelector(SELECTORS.gallery);
      if (!galleryUpdated) {
        this.replaceElement(currentSection, newSection, SELECTORS.productInfo);
      }
    }

    replaceElement(currentSection, newSection, selector) {
      // Handle multiple selectors separated by comma
      const selectors = selector.split(',').map(s => s.trim());

      for (const sel of selectors) {
        const currentEl = currentSection.querySelector(sel);
        const newEl = newSection.querySelector(sel);

        if (currentEl && newEl) {
          currentEl.innerHTML = newEl.innerHTML;

          // Copy over data attributes
          Array.from(newEl.attributes).forEach(attr => {
            if (attr.name.startsWith('data-')) {
              currentEl.setAttribute(attr.name, attr.value);
            }
          });

          return true;
        }
      }

      return false;
    }

    updateActiveTab(clickedTab) {
      // Remove active class from all tabs
      this.tabs.forEach(tab => {
        tab.classList.remove('active');
        tab.removeAttribute('aria-current');
      });

      // Add active class to clicked tab
      clickedTab.classList.add('active');
      clickedTab.setAttribute('aria-current', 'true');
    }

    reinitializeComponents(originalStraightToCheckout = null) {
      // 1. Clear upsell state from previous product
      // getState() returns a shallow copy but upsells is a shared reference
      if (window.PBFCM_PDP && window.PBFCM_PDP.getState) {
        const currentState = window.PBFCM_PDP.getState();
        if (currentState.upsells) {
          // Clear all upsell quantities from the shared upsells object
          Object.keys(currentState.upsells).forEach(key => {
            delete currentState.upsells[key];
          });
        }
      }

      // 2. Restore straight-to-checkout BEFORE init() reads it
      // This is a page-level metafield that gets lost when fetching via product URL
      if (originalStraightToCheckout === 'true' || originalStraightToCheckout === '1') {
        const stcEl = document.getElementById('PBFCM-straight-to-checkout');
        if (stcEl) stcEl.value = originalStraightToCheckout;
      }

      // 3. Reinitialize PBFCM_PDP to reload config from new hidden fields
      // This updates variantId, productId, sellingPlanId, and prices from DOM
      // Now it will correctly read the restored straight_to_checkout value
      if (window.PBFCM_PDP && window.PBFCM_PDP.init) {
        window.PBFCM_PDP.init();
      }

      // 3b. Reinitialize gift progress to refresh DOM references
      if (window.PBFCM_PDP && window.PBFCM_PDP.giftProgress &&
          window.PBFCM_PDP.giftProgress.init) {
        window.PBFCM_PDP.giftProgress.init();
      }

      // 3. Reinitialize product gallery (Swiper sliders, etc.)
      if (window.PBFCM_PDP && window.PBFCM_PDP.productGallery &&
          window.PBFCM_PDP.productGallery.init) {
        window.PBFCM_PDP.productGallery.init();
      }

      // Reinitialize Shopify payment buttons
      if (window.Shopify && window.Shopify.PaymentButton) {
        window.Shopify.PaymentButton.init();
      }

      // Reinitialize product form variant handling
      const variantSelects = document.querySelector(`#shopify-section-${this.sectionId} variant-selects`);
      if (variantSelects && typeof variantSelects.updateOptions === 'function') {
        variantSelects.updateOptions();
      }

      // Dispatch custom event for other scripts to listen to
      document.dispatchEvent(new CustomEvent('multi-product-tabs:switched', {
        detail: {
          sectionId: this.sectionId
        }
      }));

      // Reinitialize product gallery if PBFCM gallery exists
      const gallery = document.querySelector(`#shopify-section-${this.sectionId} .PBFCM-PDP-product-gallery`);
      if (gallery && window.PBFCM && window.PBFCM.initGallery) {
        window.PBFCM.initGallery(gallery);
      }

      // Trigger any Swiper/slider reinitializations
      if (window.Swiper) {
        const swiperContainers = document.querySelectorAll(`#shopify-section-${this.sectionId} .swiper`);
        swiperContainers.forEach(container => {
          if (container.swiper) {
            container.swiper.update();
          }
        });
      }
    }

    setLoading(isLoading) {
      this.isLoading = isLoading;
      this.container.classList.toggle('loading', isLoading);

      // Disable/enable tabs during loading
      this.tabs.forEach(tab => {
        tab.disabled = isLoading;
      });
    }
  }

  // Initialize all tab containers on page load
  function init() {
    const tabContainers = document.querySelectorAll(SELECTORS.tabsContainer);
    tabContainers.forEach(container => {
      new MultiProductTabs(container);
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize after section rendering (for theme editor)
  document.addEventListener('shopify:section:load', (event) => {
    const tabContainers = event.target.querySelectorAll(SELECTORS.tabsContainer);
    tabContainers.forEach(container => {
      new MultiProductTabs(container);
    });
  });
})();
